/**
 * Citizen Level
 *
 * Manages citizen level progression.
 */

import { CitizenLevel, type CitizenLevelProgress } from './types';

export { CitizenLevel };
export type { CitizenLevelProgress };

/**
 * CANONICAL SOURCE OF TRUTH for citizen-level miles thresholds + names.
 *
 * Do NOT duplicate these numbers elsewhere. `@tonnie/assets` owns only the
 * badge image assets + display copy; `apps/platform-api` passport services and
 * `apps/citizen-mobile` import these constants from here. The data-integrity
 * validation oracle asserts these values against its golden spec, so any drift
 * fails the suite loudly.
 */
export const CITIZEN_LEVEL_THRESHOLDS: Record<CitizenLevel, number> = {
  [CitizenLevel.Newcomer]: 0,
  [CitizenLevel.Explorer]: 500,
  [CitizenLevel.Voyager]: 1500,
  [CitizenLevel.Pioneer]: 3000,
  [CitizenLevel.Ambassador]: 5000,
};

export const CITIZEN_LEVEL_NAMES: Record<CitizenLevel, string> = {
  [CitizenLevel.Newcomer]: 'Newcomer',
  [CitizenLevel.Explorer]: 'Explorer',
  [CitizenLevel.Voyager]: 'Voyager',
  [CitizenLevel.Pioneer]: 'Pioneer',
  [CitizenLevel.Ambassador]: 'Ambassador',
};

// Descending [level, threshold] pairs — single source the calc derives from.
const LEVELS_DESC = (Object.keys(CITIZEN_LEVEL_THRESHOLDS) as unknown as CitizenLevel[])
  .map((k) => Number(k) as CitizenLevel)
  .sort((a, b) => b - a);

/**
 * Calculates citizen level from total miles.
 * Pure function - no tenant context needed. Derives from
 * CITIZEN_LEVEL_THRESHOLDS so there is exactly one place the numbers live.
 */
export function calculateCitizenLevel(totalMiles: number): CitizenLevel {
  for (const level of LEVELS_DESC) {
    if (totalMiles >= CITIZEN_LEVEL_THRESHOLDS[level]) return level;
  }
  return CitizenLevel.Newcomer;
}

/**
 * Gets progress to next citizen level.
 * Pure function.
 */
export function getProgressToNextLevel(totalMiles: number): CitizenLevelProgress {
  const currentLevel = calculateCitizenLevel(totalMiles);

  if (currentLevel === CitizenLevel.Ambassador) {
    return {
      currentLevel,
      nextLevel: null,
      progress: 100,
      milesRemaining: 0,
    };
  }

  const nextLevel = (currentLevel + 1) as CitizenLevel;
  const currentThreshold = CITIZEN_LEVEL_THRESHOLDS[currentLevel];
  const nextThreshold = CITIZEN_LEVEL_THRESHOLDS[nextLevel];
  const range = nextThreshold - currentThreshold;
  const progressMiles = totalMiles - currentThreshold;

  return {
    currentLevel,
    nextLevel,
    progress: Math.round((progressMiles / range) * 100),
    milesRemaining: nextThreshold - totalMiles,
  };
}

/**
 * Gets display name for citizen level.
 * Pure function.
 */
export function getCitizenLevelName(level: CitizenLevel): string {
  return CITIZEN_LEVEL_NAMES[level];
}

/** One rung of the citizen-level ladder, as a surface draws it. */
export interface CitizenLevelRung {
  level: CitizenLevel;
  milesThreshold: number;
  achieved: boolean;
  current: boolean;
  /** Miles still to earn to reach this level; 0 once reached. */
  milesToReach: number;
}

/** The citizen's level, their progress to the next, and every level on the way. */
export interface CitizenLevelLadder extends CitizenLevelProgress {
  levels: CitizenLevelRung[];
}

/**
 * The citizen-level ladder for a miles balance — computed by the SERVER and
 * sent with the balance, so every surface (the citizen Passport on web and
 * phone, the company's drawer and triad) draws the same level. Each surface
 * used to rebuild it from the balance on its own.
 */
export function citizenLevelLadder(totalMiles: number): CitizenLevelLadder {
  const progress = getProgressToNextLevel(totalMiles);
  const levels = (Object.keys(CITIZEN_LEVEL_THRESHOLDS) as unknown as CitizenLevel[])
    .map((k) => Number(k) as CitizenLevel)
    .sort((a, b) => a - b)
    .map((level) => ({
      level,
      milesThreshold: CITIZEN_LEVEL_THRESHOLDS[level],
      achieved: totalMiles >= CITIZEN_LEVEL_THRESHOLDS[level],
      current: level === progress.currentLevel,
      milesToReach: Math.max(0, CITIZEN_LEVEL_THRESHOLDS[level] - totalMiles),
    }));
  return { ...progress, levels };
}

/** The i18n key of each level — guarded against en.json in dynamic-key-coverage.spec.ts. */
export const CITIZEN_LEVEL_LABEL_KEY = {
  [CitizenLevel.Newcomer]: 'passport.levels.newcomer',
  [CitizenLevel.Explorer]: 'passport.levels.explorer',
  [CitizenLevel.Voyager]: 'passport.levels.voyager',
  [CitizenLevel.Pioneer]: 'passport.levels.pioneer',
  [CitizenLevel.Ambassador]: 'passport.levels.ambassador',
} as const satisfies Record<CitizenLevel, string>;
